import * as React from 'react';
import { cn } from '@/lib/utils';
import { Inbox } from 'lucide-react';
import { premiumTheme } from './tokens';

/**
 * PremiumEmptyState \u2014 Calm, structured empty state.
 *
 * Use for empty tables, no-activity feeds, zero-case patients, etc.
 * Encourages a single primary action.
 */

export interface PremiumEmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional icon (defaults to `Inbox`). Pass `null` to hide. */
  icon?: React.ReactNode | null;
  /** Primary CTA */
  action?: React.ReactNode;
  /** Secondary CTA */
  secondaryAction?: React.ReactNode;
  /** Compact variant (for inline empty panels inside cards) */
  compact?: boolean;
}

export const PremiumEmptyState = React.forwardRef<
  HTMLDivElement,
  PremiumEmptyStateProps
>(
  (
    {
      title,
      description,
      icon,
      action,
      secondaryAction,
      compact = false,
      className,
      ...props
    },
    ref,
  ) => {
    const resolvedIcon =
      icon === null ? null : icon ?? <Inbox aria-hidden="true" />;

    return (
      <div
        ref={ref}
        role="status"
        className={cn(
          'flex flex-col items-center justify-center text-center',
          compact ? 'py-6 px-4 gap-2' : 'py-10 px-6 gap-3',
          className,
        )}
        {...props}
      >
        {resolvedIcon && (
          <div
            className={cn(
              'flex items-center justify-center rounded-full bg-surface-premium-sunken text-muted-foreground',
              compact ? 'h-8 w-8 [&>svg]:h-4 [&>svg]:w-4' : 'h-12 w-12 [&>svg]:h-5 [&>svg]:w-5',
            )}
            aria-hidden="true"
          >
            {resolvedIcon}
          </div>
        )}
        <div className="max-w-sm">
          <div className={premiumTheme.typography.title}>{title}</div>
          {description && (
            <p className={cn(premiumTheme.typography.subtitle, 'mt-1')}>
              {description}
            </p>
          )}
        </div>
        {(action || secondaryAction) && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
            {action}
            {secondaryAction}
          </div>
        )}
      </div>
    );
  },
);
PremiumEmptyState.displayName = 'PremiumEmptyState';
